<template>
  <section>
    <page-sub-header title="Rewards" class="mt-4">
      <template #header>
        <div class="d-flex flex-row align-items-center justify-content-end">
          <app-input
            v-model="timeSeries"
            :options="TIME_SERIES_VALUES"
            type="radio"
            buttons
            button-variant="outline-primary"
            class="text-uppercase mb-0"
            size="sm"
          />
          <export-button
            v-if="isDataReady"
            :file-name="`rewards-metrics-${timeSeries}`"
            :export-data="exportData"
            :export-file-rows="exportFileRows"
            :export-headers="exportHeaders"
            class="btn mt-0"
          />
        </div>
      </template>
    </page-sub-header>
    <sme-card v-if="loading">
      <app-loading />
    </sme-card>
    <template v-else>
      <!-- Counter Cards Row -->
      <b-row>
        <b-col cols="12" md="4" class="mb-4">
          <sme-card class="h-100 overflow-hidden">
            <app-stat
              :stat="totalRewardsUsers"
              :formatter="toInteger"
              description="Rewards Users"
              class="mb-2"
              background-color="core-1-lighten-90"
              color="default"
              small
            />
            <line-chart
              v-if="rewardsUsersDataset.values.length"
              :labels="timeGroupDataset"
              :datasets="timeGroupedRewardsUsersDataset"
              :chart-options="CHART_PRESET_OPTIONS.MINIMAL"
              :display-points="false"
              class="mt-auto"
              fill-gradient
            />
          </sme-card>
        </b-col>
        <b-col cols="12" md="4" class="mb-4">
          <sme-card class="h-100 overflow-hidden">
            <app-stat
              :stat="totalSavings"
              :formatter="value => toInteger(Math.round(value))"
              :prefix="currencySymbol"
              description="Total Savings (vouchers & cashback)"
              class="mb-2"
              background-color="core-5-lighten-90"
              color="default"
              small
            />
            <line-chart
              v-if="totalRewardsSavingsDataset.values.length"
              :labels="timeGroupDataset"
              :datasets="timeGroupedTotalSavingsDataset"
              :chart-options="merge({}, CHART_PRESET_OPTIONS.CURRENCY, CHART_PRESET_OPTIONS.MINIMAL)"
              :display-points="false"
              class="mt-auto"
              fill-gradient
            />
          </sme-card>
        </b-col>
        <b-col cols="12" md="4" class="mb-4">
          <sme-card class="h-100 overflow-hidden">
            <app-stat
              :stat="totalAccessed"
              :formatter="toInteger"
              description="Total Accessed"
              class="mb-2"
              background-color="core-3-lighten-90"
              color="default"
              small
            />
            <line-chart
              v-if="totalAccessedDataset.values.length"
              :labels="timeGroupDataset"
              :datasets="timeGroupedTotalAccessedDataset"
              :chart-options="CHART_PRESET_OPTIONS.MINIMAL"
              :display-points="false"
              class="mt-auto"
              fill-gradient
            />
          </sme-card>
        </b-col>
      </b-row>

      <!-- Trending Brands Section -->
      <sme-card v-if="trendingBrands.length" class="mb-4">
        <h6 class="mb-3">Trending Brands</h6>
        <b-row>
          <b-col v-for="(brand, index) in trendingBrands" :key="brand.retailer" cols="6" md="4" lg="3" class="mb-2">
            <div class="d-flex align-items-center">
              <span class="text-muted mr-2">{{ index + 1 }}.</span>
              <span class="text-truncate">{{ brand.retailer }}</span>
              <span class="text-muted ml-auto">({{ toInteger(brand.count) }})</span>
            </div>
          </b-col>
        </b-row>
      </sme-card>

      <!-- Empty State -->
      <sme-card v-if="!hasData" class="text-center py-4">
        <p class="text-muted mb-0">No rewards data available for the selected period.</p>
      </sme-card>
    </template>
  </section>
</template>

<script setup>
import merge from 'lodash/merge';
import { computed, onBeforeMount, ref, watch } from 'vue';
import ApiClient from '@/ApiClient';
import { PaletteColors } from '@/Theme';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppStat from '@/components/AppStat.vue';
import PageSubHeader from '@/components/PageSubHeader.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import LineChart, { CHART_PRESET_OPTIONS } from '@/components/charts/LineChart.vue';
import ExportButton from '@/components/ExportButton.vue';
import useTimeGroupDataset from '@/composables/useTimeGroupDataset';
import State from '@/state/State';
import useDivisionSelection from '@/state/composables/useDivisionSelection';
import { TIME_SERIES_VALUES } from '@/utils/chart';
import { toInteger, currencySymbolFromCode } from '@/utils/common';

const { divisionId } = useDivisionSelection();

const loading = ref(true);
const timeDataset = ref(null);
const voucherUsersDataset = ref({ values: [], name: 'Voucher users' });
const discountUsersDataset = ref({ values: [], name: 'Discount users' });
const voucherSavingsDataset = ref({ values: [], name: 'Voucher savings' });
const discountCashbackDataset = ref({ values: [], name: 'Discount cashback' });
const totalRewardsSavingsDataset = ref({ values: [], name: 'Total rewards savings' });
const vouchersPurchasedDataset = ref({ values: [], name: 'Vouchers purchased' });
const discountsUsedDataset = ref({ values: [], name: 'Discounts used' });
const trendingBrands = ref([]);

const { timeGroupDataset, timeSeries, datasetToTimeGroupedDataset } = useTimeGroupDataset(timeDataset);

const currencySymbol = computed(() => currencySymbolFromCode(State.state.company.default_currency));

// Computed datasets for charts
const rewardsUsersDataset = computed(() => {
  const vouchers = voucherUsersDataset.value.values || [];
  const discounts = discountUsersDataset.value.values || [];
  const values = vouchers.map((v, i) => Math.max(v || 0, discounts[i] || 0));
  return { values, name: 'Rewards users', lineColor: PaletteColors['core-1'] };
});

const totalAccessedDataset = computed(() => {
  const vouchers = vouchersPurchasedDataset.value.values || [];
  const discounts = discountsUsedDataset.value.values || [];
  const values = vouchers.map((v, i) => (v || 0) + (discounts[i] || 0));
  return { values, name: 'Total accessed', lineColor: PaletteColors['core-3'] };
});

const timeGroupedRewardsUsersDataset = computed(() => datasetToTimeGroupedDataset(rewardsUsersDataset));
const timeGroupedTotalSavingsDataset = computed(() => datasetToTimeGroupedDataset(totalRewardsSavingsDataset));
const timeGroupedTotalAccessedDataset = computed(() => datasetToTimeGroupedDataset(totalAccessedDataset));

// Computed stats
const totalRewardsUsers = computed(() => {
  const values = timeGroupedRewardsUsersDataset.value?.values || [];
  return Math.max(...values, 0);
});

const totalSavings = computed(() => {
  const values = timeGroupedTotalSavingsDataset.value?.values || [];
  return values.reduce((sum, val) => sum + (val || 0), 0);
});

const totalVouchers = computed(() => {
  const values = vouchersPurchasedDataset.value.values || [];
  return values.reduce((sum, val) => sum + (val || 0), 0);
});

const totalDiscounts = computed(() => {
  const values = discountsUsedDataset.value.values || [];
  return values.reduce((sum, val) => sum + (val || 0), 0);
});

const totalAccessed = computed(() => totalVouchers.value + totalDiscounts.value);

const hasData = computed(() => {
  return (
    rewardsUsersDataset.value.values.some(v => v > 0) ||
    totalRewardsSavingsDataset.value.values.some(v => v > 0) ||
    totalAccessedDataset.value.values.some(v => v > 0) ||
    trendingBrands.value.length > 0
  );
});

const isDataReady = computed(() => {
  return timeGroupDataset.value?.values && !loading.value;
});

const exportData = computed(() => {
  if (!isDataReady.value) {
    return [];
  }

  return timeGroupDataset.value.values.map((time, index) => ({
    time,
    rewardsUsers: rewardsUsersDataset.value.values[index] || 0,
    voucherUsers: voucherUsersDataset.value.values[index] || 0,
    discountUsers: discountUsersDataset.value.values[index] || 0,
    totalSavings: totalRewardsSavingsDataset.value.values[index] || 0,
    voucherSavings: voucherSavingsDataset.value.values[index] || 0,
    discountCashback: discountCashbackDataset.value.values[index] || 0,
    vouchersPurchased: vouchersPurchasedDataset.value.values[index] || 0,
    discountsUsed: discountsUsedDataset.value.values[index] || 0,
  }));
});

const exportHeaders = computed(() => {
  if (!isDataReady.value) {
    return [];
  }
  return [
    'Date',
    'Rewards Users',
    'Voucher Users',
    'Discount Users',
    'Total Savings (vouchers & cashback)',
    'Voucher Savings',
    'Discount Cashback',
    'Vouchers Purchased',
    'Discounts Used',
  ];
});

const exportFileRows = item => [
  item.time,
  item.rewardsUsers,
  item.voucherUsers,
  item.discountUsers,
  Math.round(item.totalSavings || 0),
  Math.round(item.voucherSavings || 0),
  Math.round(item.discountCashback || 0),
  item.vouchersPurchased,
  item.discountsUsed,
];

watch([divisionId, timeSeries], () => getRewardsMetrics());

const getRewardsMetrics = async () => {
  loading.value = true;

  try {
    const [usersResponse, savingsResponse, accessedResponse, trendingResponse] = await Promise.all([
      ApiClient.getPortalRewardsUsers(timeSeries.value, divisionId.value),
      ApiClient.getPortalRewardsSavings(timeSeries.value, divisionId.value),
      ApiClient.getPortalRewardsAccessed(timeSeries.value, divisionId.value),
      ApiClient.getPortalTrending(divisionId.value),
    ]);

    // Process users data
    if (usersResponse?.results?.length) {
      const time = usersResponse.results.find(result => result.id === 'time');
      const voucherUsers = usersResponse.results.find(result => result.id === 'voucher_users');
      const discountUsers = usersResponse.results.find(result => result.id === 'discount_users');

      if (time) {
        timeDataset.value = time;
      }
      if (voucherUsers) {
        voucherUsersDataset.value = {
          ...voucherUsers,
          name: 'Voucher users',
          lineColor: PaletteColors['core-1'],
        };
      }
      if (discountUsers) {
        discountUsersDataset.value = {
          ...discountUsers,
          name: 'Discount users',
          lineColor: PaletteColors['core-2'],
        };
      }
    }

    // Process savings data
    if (savingsResponse?.results?.length) {
      const voucherSavings = savingsResponse.results.find(result => result.id === 'voucher_savings');
      const discountCashback = savingsResponse.results.find(result => result.id === 'discount_cashback');
      const totalSavings = savingsResponse.results.find(result => result.id === 'total_rewards_savings');

      if (voucherSavings) {
        voucherSavingsDataset.value = {
          ...voucherSavings,
          name: 'Voucher savings',
          lineColor: PaletteColors['core-5'],
        };
      }
      if (discountCashback) {
        discountCashbackDataset.value = {
          ...discountCashback,
          name: 'Discount cashback',
          lineColor: PaletteColors['core-4'],
        };
      }
      if (totalSavings) {
        totalRewardsSavingsDataset.value = {
          ...totalSavings,
          name: 'Total rewards savings',
          lineColor: PaletteColors['core-5'],
        };
      }
    }

    // Process accessed data
    if (accessedResponse?.results?.length) {
      const vouchersPurchased = accessedResponse.results.find(result => result.id === 'vouchers_purchased');
      const discountsUsed = accessedResponse.results.find(result => result.id === 'discounts_used');

      if (vouchersPurchased) {
        vouchersPurchasedDataset.value = {
          ...vouchersPurchased,
          name: 'Vouchers purchased',
          lineColor: PaletteColors['core-3'],
        };
      }
      if (discountsUsed) {
        discountsUsedDataset.value = {
          ...discountsUsed,
          name: 'Discounts used',
          lineColor: PaletteColors['core-3'],
        };
      }
    }

    // Process trending data
    if (trendingResponse?.data?.length) {
      trendingBrands.value = trendingResponse.data.slice(0, 8);
    } else {
      trendingBrands.value = [];
    }
  } catch (error) {
    console.error('Failed to fetch rewards metrics:', error);
  }

  loading.value = false;
};

onBeforeMount(() => getRewardsMetrics());
</script>
